"use client";

import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useColorScheme } from "@mui/material/styles";
import { Dashboard, Assessment, AutoAwesome } from "@mui/icons-material";

const FeaturesSection = styled(Box)<{ isDarkMode: boolean }>(
  ({ theme, isDarkMode }) => ({
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),
    backgroundColor: isDarkMode ? "#0d1117" : theme.palette.background.default,
  })
);

const FeatureCard = styled(Card)<{ isDarkMode: boolean }>(
  ({ theme, isDarkMode }) => ({
    height: "100%",
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    border: isDarkMode
      ? "1px solid rgba(66, 166, 246, 0.2)"
      : "1px solid rgba(0, 0, 0, 0.05)",
    boxShadow: isDarkMode
      ? "0 4px 20px rgba(0, 0, 0, 0.3)"
      : "0 4px 20px rgba(0, 0, 0, 0.08)",
    backgroundColor: isDarkMode ? "#1a1a1a" : "white",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: isDarkMode
        ? "0 12px 40px rgba(66, 166, 246, 0.2)"
        : "0 12px 40px rgba(0, 0, 0, 0.15)",
      ...(isDarkMode && {
        borderColor: "rgba(66, 166, 246, 0.4)",
      }),
    },
  })
);

const IconWrapper = styled(Box)<{ isDarkMode: boolean }>(
  ({ theme, isDarkMode }) => ({
    width: 60,
    height: 60,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    backgroundColor: isDarkMode ? "#42A6F6" : "#42A6F6",
    color: isDarkMode ? "#1a1a1a" : "white",
  })
);

const Features: React.FC = () => {
  const { mode, systemMode } = useColorScheme();
  const isDarkMode =
    mode === "dark" || (mode === "system" && systemMode === "dark");
  const features = [
    {
      icon: <Dashboard fontSize="large" />,
      title: "Case dashboard",
      description:
        "Track financial metrics, tasks, and timelines — all in one place.",
    },
    {
      icon: <Assessment fontSize="large" />,
      title: "Court-ready reporting",
      description:
        "Cut down hours of work on 13-week cash flows and statutory reporting.",
    },
    {
      icon: <AutoAwesome fontSize="large" />,
      title: "Tool consolidation",
      description:
        "Track time, store and share key files, and stay organized across parties.",
    },
  ];

  return (
    <FeaturesSection isDarkMode={isDarkMode}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 600,
                color: isDarkMode ? "#ffffff" : "#313132",
              }}
            >
              What you get with Bouncepoint
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <FeatureCard isDarkMode={isDarkMode}>
                  <CardContent sx={{ p: 0, textAlign: "center" }}>
                    <IconWrapper
                      isDarkMode={isDarkMode}
                      sx={{ margin: "0 auto", mb: 2 }}
                    >
                      {feature.icon}
                    </IconWrapper>
                    <Typography
                      variant="h4"
                      component="h3"
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 600,
                        mb: 2,
                        color: isDarkMode ? "#ffffff" : "text.primary",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: isDarkMode ? "#b8b8b8" : "text.secondary",
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>

          <Box
            textAlign="center"
            sx={{
              mt: 8,
              pt: 6,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "100%",
                height: "1px",
                background: isDarkMode
                  ? "rgba(66, 166, 246, 0.3)"
                  : "rgba(66, 166, 246, 0.3)",
                zIndex: 0,
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "12px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "80%",
                height: "1px",
                background: isDarkMode
                  ? "rgba(66, 166, 246, 0.25)"
                  : "rgba(66, 166, 246, 0.25)",
                zIndex: 0,
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-36px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "60%",
                  height: "1px",
                  background: isDarkMode
                    ? "rgba(66, 166, 246, 0.2)"
                    : "rgba(66, 166, 246, 0.2)",
                  zIndex: 0,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "-24px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "40%",
                  height: "1px",
                  background: isDarkMode
                    ? "rgba(66, 166, 246, 0.15)"
                    : "rgba(66, 166, 246, 0.15)",
                  zIndex: 0,
                },
              }}
            >
              <Typography
                variant="h2"
                component="h3"
                sx={{
                  fontSize: { xs: "1.8rem", md: "2.2rem" },
                  fontWeight: 700,
                  color: "#F49045",
                  letterSpacing: "-0.01em",
                  position: "relative",
                  zIndex: 1,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 60,
                    height: 3,
                    backgroundColor: "#F49045",
                    borderRadius: 2,
                  },
                }}
              >
                All powered by AI
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </FeaturesSection>
  );
};

export default Features;
